/**
 * H2 — what may leave this browser, and what must never be reported at all.
 *
 * Pure, framework-free, and deliberately free of any VALUE import of
 * `@sentry/nextjs` (the type import below is erased at compile time). That is
 * what lets the whole surface be unit-tested in jsdom without loading the SDK,
 * and what keeps Sentry optional: nothing here needs an initialised client.
 *
 * Two jobs, in this order:
 *
 *  1. `isExpectedRejection` — drop deliberate rejections. tRPC `CONFLICT` /
 *     `BAD_REQUEST` / `NOT_FOUND` / `UNAUTHORIZED`, and the booking engine's
 *     own tokens, are NORMAL behaviour reaching the client.
 *  2. `scrubEvent` — strip personal data. This platform holds Israeli
 *     customers' names, phone numbers, emails and addresses, and `/b/<slug>`
 *     collects them from the public. A phone number is the customer LOGIN
 *     identifier here, so it is the highest-value thing in the payload.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ WHICH HALF IS ACTUALLY LOAD-BEARING — verified 2026-08-25, read before
 * citing this file as quota protection
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Job 2 is live on every event. Job 1 currently fires for nothing**, because
 * no tRPC error reaches the browser SDK at all. Four independent reasons, each
 * checked rather than assumed:
 *
 *  - **Every mutation that can throw a booking token is caught.** The public
 *    booking create — the one call that produces `slot-unavailable` — is inside
 *    a try/catch that renders the message as text
 *    (`app/(public)/b/[slug]/business-page.tsx`, the `catch` on the OTP-verify
 *    step). A caught rejection never becomes an unhandled one, and Sentry's
 *    `globalHandlers` integration only sees unhandled ones.
 *  - **Exactly ONE uncaught `mutateAsync` exists in the whole app**: the
 *    add-to-calendar handler in the same file. `booking.exportIcs` cannot throw
 *    a collision token, so even that one carries none of the codes above.
 *    (`login/page.tsx`'s `routeAfterVerify` looks uncaught in isolation; all
 *    three of its call sites wrap it in try/catch.)
 *  - **Queries never throw.** `trpc.tsx` sets `retry: false`; React Query puts
 *    the error on `query.error` for the component to render.
 *  - **`apps/web` makes no server-side tRPC calls**, so `instrumentation.ts`'s
 *    `onRequestError` cannot carry one either.
 *
 * The quota argument — "a busy Saturday of double-booking attempts would spend
 * 5,000 events a month" — is REAL, but it is the API's filter that collects on
 * it, not this one. `apps/api/src/observability/sentry-filter.ts` runs from
 * `app.bootstrap.ts`'s tRPC `onError`, which fires for every failure including
 * the expected ones, and that is where a `slot-unavailable` is actually
 * dropped. This file's job 1 is a second line that has never had to fire.
 *
 * **Keep it anyway.** It costs one set lookup per event, and one future
 * uncaught `mutateAsync` on a booking path turns it live with no other change.
 * What must NOT continue is claiming it protects the quota today.
 *
 * Filter first, scrub second, on purpose: the booking engine's enriched
 * conflict message is
 * `slot-unavailable:overlap-confirm:{"staffName":…,"clientName":…}` (see
 * `calendar.test.tsx`'s `overlapConflictMessage`). Scrubbing alone is not a
 * reliable defence for a name in Latin script, so the thing that actually keeps
 * that payload out of Sentry is the token match dropping the event whole.
 * Reordering these two calls is a data leak, not a refactor.
 *
 * ## The scrubbing strategy is DENY-BY-DEFAULT
 *
 * We do not enumerate "fields that hold a phone number" — the next form to
 * ship would not be on the list. Instead:
 *
 *  - Containers that exist to carry user input are DELETED wholesale
 *    (`request.data`, `request.cookies`, `request.headers`, `query_string`,
 *    `extra`, `user`, stack-frame locals).
 *  - What survives is an ALLOWLIST: event contexts, tag keys, breadcrumb data
 *    keys. Anything a future `setContext`/`setTag` adds is dropped unreviewed.
 *  - Whatever text remains is regex-redacted for phone numbers, email
 *    addresses, and Hebrew/Arabic free text.
 *
 * ## What this canNOT scrub — read before trusting it
 *
 * A LATIN-script name written into a message by hand:
 * `new Error(\`save failed for ${client.name}\`)` where the name is "Alice".
 * No rule here can tell that from an identifier. Nothing in `apps/web`
 * interpolates client data into an error message today, and nothing should
 * start. The Hebrew-run rule covers the same mistake in Hebrew, which is what
 * a Hebrew-first Israeli product would actually hit.
 */
import type { Breadcrumb, Event as SentryEvent, EventHint } from '@sentry/nextjs';

// ---------------------------------------------------------------------------
// 1. Expected rejections
// ---------------------------------------------------------------------------

/**
 * tRPC error codes that mean "the server answered correctly and said no".
 *
 * Deliberately NOT here: `INTERNAL_SERVER_ERROR` (the api's `errorFormatter`
 * masks those, and they are exactly the crashes this tranche exists to
 * surface), `FORBIDDEN` and `TOO_MANY_REQUESTS`. Those last two are arguably
 * routine too, but a `FORBIDDEN` on web has meant a real bug before — a
 * `businessProcedure` called from customer code — so it stays visible until
 * there is evidence it is noise.
 */
export const EXPECTED_TRPC_CODES: ReadonlySet<string> = new Set([
  'CONFLICT',
  'BAD_REQUEST',
  'NOT_FOUND',
  'UNAUTHORIZED',
]);

/**
 * Booking-engine tokens. These are message PREFIXES, not codes — the api puts
 * the machine-readable token at the head of the message and the human text (or
 * a JSON payload) after it: `slot-unavailable: the requested time overlaps`,
 * `slot-unavailable:hours-override-available`,
 * `slot-unavailable:overlap-confirm:{…}`. Matching the prefix covers all three
 * shapes and any future suffix.
 *
 * Both are DB invariants firing as designed (a GiST `EXCLUDE` and an
 * advisory-lock-guarded trigger), not failures.
 */
export const EXPECTED_ERROR_TOKENS: readonly string[] = [
  'slot-unavailable',
  'duplicate-service-booking',
  // Tranche K. The platform gate's refusals: maintenance mode and the
  // minimum-version floor. Both are the server doing exactly what an admin
  // asked it to do, and both are high volume by construction — maintenance
  // refuses EVERY non-exempt request for as long as it is on. The api drops
  // these at source (`observability/sentry-filter.ts`); listed here for the
  // same reason the two above are, so that whichever path becomes live first
  // is already covered.
  'maintenance',
  'update-required',
  'feature-disabled:',
];

/** Reads a nested string without `any` and without trusting the shape. */
function readString(source: unknown, ...path: string[]): string | undefined {
  let cursor: unknown = source;
  for (const key of path) {
    if (typeof cursor !== 'object' || cursor === null) return undefined;
    cursor = (cursor as Record<string, unknown>)[key];
  }
  return typeof cursor === 'string' ? cursor : undefined;
}

/**
 * The tRPC code, wherever this particular client error is carrying it.
 *
 * `TRPCClientError` exposes `.data.code`; a raw shape arrives as
 * `.shape.data.code`; an error re-thrown by a caller keeps the original under
 * `.cause`. Checking all three is cheaper than being wrong once.
 */
export function trpcErrorCode(hint?: EventHint): string | undefined {
  const original: unknown = hint?.originalException;
  return (
    readString(original, 'data', 'code') ??
    readString(original, 'shape', 'data', 'code') ??
    readString(original, 'cause', 'data', 'code')
  );
}

/**
 * Every place a message could be hiding.
 *
 * `hint.originalException` is the real object and is checked first, but it is
 * NOT always present — an event replayed from a transport buffer, or one
 * assembled by the SDK's own global handler, may arrive with only
 * `exception.values[].value` intact. Missing the token there would let a
 * routine booking collision through.
 */
function candidateMessages(event: SentryEvent, hint?: EventHint): string[] {
  const out: string[] = [];
  const original: unknown = hint?.originalException;
  if (typeof original === 'string') out.push(original);
  const originalMessage = readString(original, 'message');
  if (originalMessage !== undefined) out.push(originalMessage);
  if (typeof event.message === 'string') out.push(event.message);
  if (typeof event.logentry?.message === 'string') out.push(event.logentry.message);
  for (const value of event.exception?.values ?? []) {
    if (typeof value.value === 'string') out.push(value.value);
  }
  return out;
}

/** True when this event is the server saying "no" the way it was designed to. */
export function isExpectedRejection(event: SentryEvent, hint?: EventHint): boolean {
  const code = trpcErrorCode(hint);
  if (code !== undefined && EXPECTED_TRPC_CODES.has(code)) return true;

  for (const message of candidateMessages(event, hint)) {
    const trimmed = message.trimStart();
    for (const token of EXPECTED_ERROR_TOKENS) {
      if (trimmed.startsWith(token)) return true;
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// 2. Redaction
// ---------------------------------------------------------------------------

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

/**
 * Any run of digits and separators. The DIGIT COUNT decides, not the shape:
 * Israeli numbers reach a browser as `0501234567`, `050-123-4567`,
 * `+972501234567` and `+972 50 123 4567`, and a shape-based pattern misses one
 * of those every time someone changes an input mask.
 */
const DIGIT_RUN_RE = /\+?\d[\d\s().\u2010-\u2015-]{4,}\d/g;

/** Seven digits is a landline; below that it is a price, a count or a status. */
const MIN_REDACTED_DIGITS = 7;

/**
 * Any run of Hebrew or Arabic script.
 *
 * This is the only handle there is on a NAME. No regex recognises "רונית לוי"
 * as a person, but this product is Hebrew-first and Israeli: the strings a
 * customer types are in Hebrew, and the strings the code throws are in English.
 * So Hebrew inside an error message is treated as user data by default.
 *
 * It is a blunt instrument and it does cost something — a deliberate Hebrew UI
 * message like `שמירת השירות נכשלה` is redacted along with everything else,
 * leaving the exception type, the stack trace and the file/line, which is what
 * a stack trace was going to be read for anyway.
 */
const HEBREW_ARABIC_RUN_RE =
  /[\u0590-\u05FF\u0600-\u06FF]+(?:[\s'"\u05F3\u05F4-]+[\u0590-\u05FF\u0600-\u06FF]+)*/g;

export const REDACTED_NUMBER = '[redacted-number]';
export const REDACTED_EMAIL = '[redacted-email]';
export const REDACTED_TEXT = '[redacted-text]';

/**
 * Redacts anything that looks like a phone number, an email address, or
 * Hebrew/Arabic free text.
 *
 * Deliberately OVER-broad on all three counts. It eats a bare `2026-08-25` date
 * and a millisecond epoch, because those are digit-for-digit indistinguishable
 * from `0501-234-567`. Losing a timestamp out of a message costs a little
 * debugging; leaking the identifier a customer logs in with costs a lot more.
 *
 * ⚠️ WHAT IT STILL MISSES: a LATIN-script name written into a message by hand —
 * `new Error(\`save failed for ${client.name}\`)` where the name is "Alice".
 * Nothing here can tell that from a variable name. The defence against it is
 * that no code in `apps/web` interpolates client data into an error message,
 * and none should start.
 */
export function redactText(text: string): string {
  return text
    .replace(EMAIL_RE, REDACTED_EMAIL)
    .replace(DIGIT_RUN_RE, (match) => {
      const digits = match.replace(/\D/g, '').length;
      return digits >= MIN_REDACTED_DIGITS ? REDACTED_NUMBER : match;
    })
    .replace(HEBREW_ARABIC_RUN_RE, REDACTED_TEXT);
}

/**
 * Keeps the path, drops the query string and the fragment.
 *
 * A query string is where a phone number, an email or an OTP would ride
 * (`?phone=…`, `?email=…`), and no route in this app needs one to be
 * identified. A fragment goes too — it never reaches a server and is a
 * convenient place for someone to have stashed state.
 */
export function stripUrl(url: string): string {
  const cut = url.search(/[?#]/);
  const base = cut === -1 ? url : url.slice(0, cut);
  return redactText(base);
}

// ---------------------------------------------------------------------------
// 3. Scrubbing an event
// ---------------------------------------------------------------------------

/**
 * Event contexts that carry no user input: device/runtime facts and the trace
 * id. Anything else — including whatever a future `Sentry.setContext` adds —
 * is dropped rather than reviewed.
 */
const ALLOWED_CONTEXTS: ReadonlySet<string> = new Set([
  'app',
  'browser',
  'culture',
  'device',
  'os',
  'runtime',
  'trace',
]);

/**
 * Breadcrumb `data` keys worth keeping. Everything else in a breadcrumb's data
 * is dropped: `fetch` and `xhr` breadcrumbs are the ones that would otherwise
 * carry a request body, and a `ui.click` on the booking form is exactly the
 * moment a name and a phone number are on screen.
 */
const ALLOWED_BREADCRUMB_DATA: ReadonlySet<string> = new Set([
  'method',
  'status_code',
  'url',
  'from',
  'to',
  'type',
  'level',
]);

/** Keys inside the allowlist that hold a URL and therefore need the query cut. */
const URL_BREADCRUMB_KEYS: ReadonlySet<string> = new Set(['url', 'from', 'to']);

/**
 * Tags the SDK sets by itself. Nothing in `apps/web` calls `Sentry.setTag`, and
 * a tag is a searchable, indexed, high-cardinality field — precisely the wrong
 * place for a customer's name to end up. So the list is closed: a tag that is
 * not on it is dropped, including any a future `setTag` introduces.
 */
const ALLOWED_TAGS: ReadonlySet<string> = new Set([
  'handled',
  'mechanism',
  'level',
  'transaction',
  'url',
  'browser',
  'browser.name',
  'os',
  'os.name',
  'runtime',
  'runtime.name',
]);

/**
 * Scrubs one breadcrumb, or drops it.
 *
 * `console` breadcrumbs are dropped outright. They capture the arguments of
 * every `console.log`, which is an unbounded channel — a debugging
 * `console.log(client)` left in a component would ship the whole client record
 * to Sentry with no way for this file to know.
 */
export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  if (breadcrumb.category === 'console') return null;

  const scrubbed: Breadcrumb = { ...breadcrumb };
  if (typeof scrubbed.message === 'string') scrubbed.message = redactText(scrubbed.message);

  if (scrubbed.data && typeof scrubbed.data === 'object') {
    const kept: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(scrubbed.data)) {
      if (!ALLOWED_BREADCRUMB_DATA.has(key)) continue;
      if (typeof value === 'string') {
        kept[key] = URL_BREADCRUMB_KEYS.has(key) ? stripUrl(value) : redactText(value);
      } else {
        kept[key] = value;
      }
    }
    scrubbed.data = kept;
  }
  return scrubbed;
}

/**
 * Removes personal data from an event, in place.
 *
 * Returns the same object so it can be used as `beforeSend`'s return value
 * directly; mutation is deliberate, since Sentry hands us the only copy.
 */
export function scrubEvent<T extends SentryEvent>(event: T): T {
  // ── Containers deleted whole. Nothing here is reviewed field by field. ──
  delete event.user; // set nowhere today; would carry ip_address by default
  delete event.extra;

  if (event.request) {
    delete event.request.data; // request bodies — the booking form itself
    delete event.request.cookies;
    delete event.request.headers; // Authorization, Cookie, X-Visitor-Id
    delete event.request.query_string;
    if (typeof event.request.url === 'string') event.request.url = stripUrl(event.request.url);
  }

  if (event.contexts) {
    for (const key of Object.keys(event.contexts)) {
      if (!ALLOWED_CONTEXTS.has(key)) delete event.contexts[key];
    }
  }

  // ── Free text that survived: redact it. ──
  if (typeof event.message === 'string') event.message = redactText(event.message);

  // `logentry` is the normalised form of a `captureMessage`. `candidateMessages`
  // above already reads it for filtering, so leaving it unscrubbed would be a
  // hole this very file knows about. `params` is dropped rather than redacted —
  // it is an interpolation array, i.e. exactly where the values go.
  if (event.logentry) {
    if (typeof event.logentry.message === 'string') {
      event.logentry.message = redactText(event.logentry.message);
    }
    delete event.logentry.params;
  }

  for (const value of event.exception?.values ?? []) {
    if (typeof value.value === 'string') value.value = redactText(value.value);
    for (const frame of value.stacktrace?.frames ?? []) {
      // Local variables. Not captured by the browser SDK, and only by an opt-in
      // integration on Node — deleted anyway, because if one ever IS captured
      // inside a booking handler it holds the whole form.
      delete frame.vars;
    }
  }

  if (event.tags) {
    for (const [key, value] of Object.entries(event.tags)) {
      if (!ALLOWED_TAGS.has(key)) {
        delete event.tags[key];
      } else if (typeof value === 'string') {
        event.tags[key] = key === 'url' ? stripUrl(value) : redactText(value);
      }
    }
  }

  // ── Breadcrumbs. ──
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs
      .map(scrubBreadcrumb)
      .filter((crumb): crumb is Breadcrumb => crumb !== null);
  }

  return event;
}

// ---------------------------------------------------------------------------
// 4. The two hooks Sentry.init actually receives
// ---------------------------------------------------------------------------

/**
 * `beforeSend`. Returning `null` discards the event entirely — it is never
 * sent and never counted against the quota.
 */
export function sentryBeforeSend<T extends SentryEvent>(event: T, hint?: EventHint): T | null {
  if (isExpectedRejection(event, hint)) return null;
  return scrubEvent(event);
}

/** `beforeBreadcrumb`. Scrubs at capture time, before the crumb is buffered. */
export function sentryBeforeBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  return scrubBreadcrumb(breadcrumb);
}
